/* Smart Flow Services - Street Level Environment (js/street.js)
   Constructs the vibrant ground-level urban environment:
   - Concrete sidewalks, curb edges, asphalt roadway & road markings
   - Street furniture: cyber lampposts with glowing warm/cyan fixtures, bollards, benches
   - New York / Cyberpunk style Hot-Dog & Coffee Stand with striped red/yellow awning, condiment bottles, neon sign
   - Corner Cafe Terrace with bistro tables, parasols, glowing espresso sign
   - Low-poly walking pedestrians & patrons animated on sidewalk paths
   - Distance background city block silhouettes with illuminated windows
*/

import * as THREE from "three";

/** Low-poly pedestrian walking figure */
function makePedestrian(accentCol, skinCol, clothesCol) {
  const figure = new THREE.Group();

  const bodyMat = new THREE.MeshStandardMaterial({
    color: clothesCol,
    roughness: 0.6,
    metalness: 0.2,
  });
  const skinMat = new THREE.MeshStandardMaterial({
    color: skinCol,
    roughness: 0.7,
  });
  const accentMat = new THREE.MeshBasicMaterial({
    color: accentCol,
    toneMapped: false,
  });

  // Torso
  const torso = new THREE.Mesh(
    new THREE.BoxGeometry(0.36, 0.55, 0.22),
    bodyMat,
  );
  torso.position.y = 0.95;

  // Head & cyber visor
  const head = new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.22, 0.2), skinMat);
  head.position.y = 1.38;
  const visor = new THREE.Mesh(
    new THREE.BoxGeometry(0.18, 0.05, 0.04),
    accentMat,
  );
  visor.position.set(0, 0.02, 0.11);
  head.add(visor);

  // Legs with pivot points
  const legGeo = new THREE.BoxGeometry(0.12, 0.65, 0.14);
  legGeo.translate(0, -0.32, 0);

  const legL = new THREE.Mesh(legGeo, bodyMat);
  legL.position.set(-0.1, 0.68, 0);
  const legR = new THREE.Mesh(legGeo, bodyMat);
  legR.position.set(0.1, 0.68, 0);

  // Arms
  const armGeo = new THREE.BoxGeometry(0.09, 0.48, 0.1);
  armGeo.translate(0, -0.22, 0);
  const armL = new THREE.Mesh(armGeo, bodyMat);
  armL.position.set(-0.24, 1.15, 0);
  const armR = new THREE.Mesh(armGeo, bodyMat);
  armR.position.set(0.24, 1.15, 0);

  figure.add(torso, head, legL, legR, armL, armR);
  figure.userData = { legL, legR, armL, armR };

  return figure;
}

/** Hot Dog Stand Model */
function makeHotDogStand() {
  const cart = new THREE.Group();

  const metalMat = new THREE.MeshStandardMaterial({
    color: 0x94a3b8,
    metalness: 0.85,
    roughness: 0.25,
  });
  const cartBodyMat = new THREE.MeshStandardMaterial({
    color: 0x0284c7, // SmartFlow blue cart body
    roughness: 0.4,
  });
  const awningMat = new THREE.MeshStandardMaterial({
    color: 0xef4444, // Red & White stripes
    roughness: 0.7,
  });
  const yellowMat = new THREE.MeshStandardMaterial({
    color: 0xfacc15,
    roughness: 0.5,
  });
  const neonSignMat = new THREE.MeshBasicMaterial({
    color: 0xf59e0b,
    toneMapped: false,
  });

  // Cart body
  const body = new THREE.Mesh(
    new THREE.BoxGeometry(2.2, 1.1, 1.4),
    cartBodyMat,
  );
  body.position.y = 0.85;

  // Countertop
  const counter = new THREE.Mesh(
    new THREE.BoxGeometry(2.4, 0.08, 1.6),
    metalMat,
  );
  counter.position.y = 1.44;

  // Wheels
  const wheelGeo = new THREE.CylinderGeometry(0.35, 0.35, 0.1, 16);
  wheelGeo.rotateZ(Math.PI / 2);
  const wheelMat = new THREE.MeshStandardMaterial({
    color: 0x111827,
    roughness: 0.8,
  });
  const wheelL = new THREE.Mesh(wheelGeo, wheelMat);
  wheelL.position.set(-1.15, 0.35, 0);
  const wheelR = new THREE.Mesh(wheelGeo, wheelMat);
  wheelR.position.set(1.15, 0.35, 0);

  // Steamer box & Grill
  const grill = new THREE.Mesh(new THREE.BoxGeometry(0.8, 0.25, 0.9), metalMat);
  grill.position.set(-0.6, 1.58, 0.1);

  // Mustard & Ketchup squeeze bottles
  const bottleRed = new THREE.Mesh(
    new THREE.CylinderGeometry(0.04, 0.04, 0.18, 8),
    awningMat,
  );
  bottleRed.position.set(0.4, 1.56, -0.4);
  const bottleYellow = new THREE.Mesh(
    new THREE.CylinderGeometry(0.04, 0.04, 0.18, 8),
    yellowMat,
  );
  bottleYellow.position.set(0.55, 1.56, -0.4);

  // Awning Canopy with 4 support poles
  const poleGeo = new THREE.CylinderGeometry(0.025, 0.025, 1.4, 8);
  for (const [px, pz] of [
    [-1.1, -0.7],
    [1.1, -0.7],
    [-1.1, 0.7],
    [1.1, 0.7],
  ]) {
    const pole = new THREE.Mesh(poleGeo, metalMat);
    pole.position.set(px, 2.15, pz);
    cart.add(pole);
  }

  const canopy = new THREE.Mesh(
    new THREE.ConeGeometry(1.7, 0.65, 4),
    awningMat,
  );
  canopy.rotation.y = Math.PI / 4;
  canopy.position.set(0, 3.1, 0);

  // Glowing neon HOT DOGS sign
  const sign = new THREE.Mesh(
    new THREE.BoxGeometry(1.2, 0.25, 0.05),
    neonSignMat,
  );
  sign.position.set(0, 2.65, 0.85);

  cart.add(
    body,
    counter,
    wheelL,
    wheelR,
    grill,
    bottleRed,
    bottleYellow,
    canopy,
    sign,
  );
  return cart;
}

/** Street Cafe Bistro Table & Chairs */
function makeCafeTerrace() {
  const cafe = new THREE.Group();

  const woodMat = new THREE.MeshStandardMaterial({
    color: 0x3b2219,
    roughness: 0.5,
  });
  const metalMat = new THREE.MeshStandardMaterial({
    color: 0x1e293b,
    metalness: 0.8,
  });
  const parasolMat = new THREE.MeshStandardMaterial({
    color: 0x0284c7,
    roughness: 0.6,
  });

  // Table
  const tableTop = new THREE.Mesh(
    new THREE.CylinderGeometry(0.65, 0.65, 0.05, 16),
    woodMat,
  );
  tableTop.position.y = 0.85;
  const tableLeg = new THREE.Mesh(
    new THREE.CylinderGeometry(0.04, 0.04, 0.85, 8),
    metalMat,
  );
  tableLeg.position.y = 0.42;
  const tableBase = new THREE.Mesh(
    new THREE.CylinderGeometry(0.3, 0.3, 0.04, 12),
    metalMat,
  );
  tableBase.position.y = 0.02;

  // Parasol umbrella
  const umbrellaPole = new THREE.Mesh(
    new THREE.CylinderGeometry(0.03, 0.03, 2.6, 8),
    metalMat,
  );
  umbrellaPole.position.y = 1.3;
  const umbrellaCone = new THREE.Mesh(
    new THREE.ConeGeometry(1.4, 0.55, 12),
    parasolMat,
  );
  umbrellaCone.position.y = 2.6;

  // 2 Chairs
  function makeChair(z, rotY) {
    const chair = new THREE.Group();
    const cSeat = new THREE.Mesh(
      new THREE.CylinderGeometry(0.24, 0.24, 0.04, 12),
      woodMat,
    );
    cSeat.position.y = 0.5;
    const cBack = new THREE.Mesh(
      new THREE.BoxGeometry(0.35, 0.35, 0.03),
      woodMat,
    );
    cBack.position.set(0, 0.75, -0.18);
    const cLeg = new THREE.Mesh(
      new THREE.CylinderGeometry(0.02, 0.02, 0.5, 6),
      metalMat,
    );
    cLeg.position.y = 0.25;
    chair.add(cSeat, cBack, cLeg);
    chair.position.set(0, 0, z);
    chair.rotation.y = rotY;
    return chair;
  }

  cafe.add(
    tableTop,
    tableLeg,
    tableBase,
    umbrellaPole,
    umbrellaCone,
    makeChair(-0.85, 0),
    makeChair(0.85, Math.PI),
  );
  return cafe;
}

/** Security Guard Officer at the lobby entrance */
function makeSecurityGuard(accentCol) {
  const guard = new THREE.Group();

  const uniformMat = new THREE.MeshStandardMaterial({
    color: 0x0f172a, // Dark obsidian navy uniform
    roughness: 0.5,
    metalness: 0.3,
  });
  const shirtMat = new THREE.MeshStandardMaterial({
    color: 0x1e293b,
    roughness: 0.6,
  });
  const skinMat = new THREE.MeshStandardMaterial({
    color: 0xd4a373,
    roughness: 0.7,
  });
  const goldMat = new THREE.MeshStandardMaterial({
    color: 0xf59e0b,
    metalness: 0.85,
    roughness: 0.25,
  });
  const badgeMat = new THREE.MeshBasicMaterial({
    color: accentCol,
    toneMapped: false,
  });

  // Torso & tactical vest
  const torso = new THREE.Mesh(
    new THREE.BoxGeometry(0.44, 0.6, 0.26),
    uniformMat,
  );
  torso.position.y = 1.0;

  // Security badge & shoulder epaulets
  const badge = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.1, 0.02), goldMat);
  badge.position.set(0.12, 1.15, 0.14);
  const patch = new THREE.Mesh(new THREE.PlaneGeometry(0.06, 0.04), badgeMat);
  patch.position.set(-0.12, 1.15, 0.14);

  // Head & security cap
  const head = new THREE.Mesh(new THREE.BoxGeometry(0.22, 0.24, 0.22), skinMat);
  head.position.y = 1.44;
  const cap = new THREE.Mesh(
    new THREE.BoxGeometry(0.26, 0.08, 0.3),
    uniformMat,
  );
  cap.position.set(0, 1.56, 0.03);
  const visor = new THREE.Mesh(
    new THREE.BoxGeometry(0.24, 0.02, 0.1),
    uniformMat,
  );
  visor.position.set(0, 1.53, 0.18);

  // Legs
  const legGeo = new THREE.BoxGeometry(0.15, 0.72, 0.18);
  const legL = new THREE.Mesh(legGeo, uniformMat);
  legL.position.set(-0.12, 0.36, 0);
  const legR = new THREE.Mesh(legGeo, uniformMat);
  legR.position.set(0.12, 0.36, 0);

  // Arms in formal standing posture
  const armGeo = new THREE.BoxGeometry(0.11, 0.52, 0.12);
  const armL = new THREE.Mesh(armGeo, uniformMat);
  armL.position.set(-0.28, 1.0, 0.02);
  const armR = new THREE.Mesh(armGeo, uniformMat);
  armR.position.set(0.28, 1.0, 0.02);

  // Security Scanner Post / Earpiece
  const earpiece = new THREE.Mesh(
    new THREE.BoxGeometry(0.03, 0.03, 0.03),
    badgeMat,
  );
  earpiece.position.set(0.12, 1.45, 0);

  guard.add(
    torso,
    badge,
    patch,
    head,
    cap,
    visor,
    legL,
    legR,
    armL,
    armR,
    earpiece,
  );
  return guard;
}

/** Grand dark-walnut paneled double doors */
function makeGrandEntrance(accentCol) {
  const entrance = new THREE.Group();

  const frameMat = new THREE.MeshStandardMaterial({
    color: 0x1a120c,
    metalness: 0.25,
    roughness: 0.55,
  });
  const woodMat = new THREE.MeshStandardMaterial({
    color: 0x3b2416,
    metalness: 0.08,
    roughness: 0.62,
  });
  const panelMat = new THREE.MeshStandardMaterial({
    color: 0x2a1810,
    metalness: 0.06,
    roughness: 0.7,
  });
  const brassMat = new THREE.MeshStandardMaterial({
    color: 0x8a6a32,
    metalness: 0.75,
    roughness: 0.35,
  });
  const neonCyan = new THREE.MeshBasicMaterial({
    color: accentCol,
    toneMapped: false,
  });

  const leftCol = new THREE.Mesh(
    new THREE.BoxGeometry(0.7, 8.6, 0.85),
    frameMat,
  );
  leftCol.position.set(-4.55, 4.3, 0);
  const rightCol = leftCol.clone();
  rightCol.position.x = 4.55;
  const header = new THREE.Mesh(
    new THREE.BoxGeometry(11.4, 0.7, 0.95),
    frameMat,
  );
  header.position.set(0, 8.55, 0);
  const headerTrim = new THREE.Mesh(
    new THREE.BoxGeometry(10.8, 0.06, 0.98),
    brassMat,
  );
  headerTrim.position.set(0, 8.22, 0);
  const haloStrip = new THREE.Mesh(
    new THREE.BoxGeometry(9.8, 0.035, 0.04),
    neonCyan,
  );
  haloStrip.position.set(0, 8.95, 0.46);

  // Fixed sidelight panels - seal the gap between the open door leaves and the columns
  function makeSidelight(side) {
    const sg = new THREE.Group();
    const panel = new THREE.Mesh(
      new THREE.BoxGeometry(1.3, 7.7, 0.16),
      woodMat,
    );
    panel.position.y = 3.9;
    const border = new THREE.Mesh(
      new THREE.BoxGeometry(1.38, 7.84, 0.08),
      frameMat,
    );
    border.position.y = 3.9;
    const bead = new THREE.Mesh(
      new THREE.BoxGeometry(1.18, 7.6, 0.05),
      panelMat,
    );
    bead.position.y = 3.9;
    sg.add(border, panel, bead);
    sg.position.set(side * 3.55, 0, -0.04);
    return sg;
  }

  // Jamb filler strips - close the gap between the columns and the lobby front walls
  function makeJambFiller(side) {
    const filler = new THREE.Mesh(
      new THREE.BoxGeometry(0.62, 8.3, 0.85),
      frameMat,
    );
    filler.position.set(side * 5.21, 4.15, 0);
    return filler;
  }

  function makeDoorLeaf(side) {
    const leaf = new THREE.Group();
    const slab = new THREE.Mesh(new THREE.BoxGeometry(3.0, 7.7, 0.14), woodMat);
    slab.position.y = 3.9;
    const border = new THREE.Mesh(
      new THREE.BoxGeometry(3.12, 7.84, 0.08),
      frameMat,
    );
    border.position.y = 3.9;
    leaf.add(border, slab);

    const cols = 2;
    const rows = 4;
    const pw = 1.3;
    const ph = 1.5;
    const gapX = 0.18;
    const gapY = 0.16;
    const startX = -((cols - 1) * (pw + gapX)) / 2;
    const startY = 1.1;
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const recess = new THREE.Mesh(
          new THREE.BoxGeometry(pw, ph, 0.04),
          panelMat,
        );
        recess.position.set(
          startX + c * (pw + gapX),
          startY + r * (ph + gapY),
          0.06,
        );
        const bead = new THREE.Mesh(
          new THREE.BoxGeometry(pw + 0.1, ph + 0.1, 0.03),
          woodMat,
        );
        bead.position.set(recess.position.x, recess.position.y, 0.045);
        leaf.add(bead, recess);
      }
    }

    const handlePost = new THREE.Mesh(
      new THREE.CylinderGeometry(0.035, 0.035, 1.15, 10),
      brassMat,
    );
    handlePost.position.set(side * -1.2, 2.35, 0.16);
    const handleKnob = new THREE.Mesh(
      new THREE.SphereGeometry(0.07, 10, 10),
      brassMat,
    );
    handleKnob.position.set(side * -1.2, 1.82, 0.2);
    leaf.add(handlePost, handleKnob);

    // Slightly open so the lobby stays visible; the outer edge tucks behind the sidelight
    leaf.position.set(side * 1.6, 0, 0);
    leaf.rotation.y = side * -0.35;
    return leaf;
  }

  const step = new THREE.Mesh(new THREE.BoxGeometry(11.2, 0.14, 1.2), frameMat);
  step.position.set(0, 0.07, 0.4);

  entrance.add(
    leftCol,
    rightCol,
    header,
    headerTrim,
    haloStrip,
    makeSidelight(-1),
    makeSidelight(1),
    makeJambFiller(-1),
    makeJambFiller(1),
    makeDoorLeaf(-1),
    makeDoorLeaf(1),
    step,
  );
  return entrance;
}

/** Compact reading lounge: sofa, armchair, coffee table, open books */
function makeLoungeArea(accentCol) {
  const lounge = new THREE.Group();

  const leatherMat = new THREE.MeshStandardMaterial({
    color: 0x1a2233,
    roughness: 0.48,
    metalness: 0.18,
  });
  const cushionMat = new THREE.MeshStandardMaterial({
    color: 0x334155,
    roughness: 0.72,
  });
  const goldMat = new THREE.MeshStandardMaterial({
    color: 0xf59e0b,
    metalness: 0.85,
    roughness: 0.25,
  });
  const marbleTableMat = new THREE.MeshStandardMaterial({
    color: 0x111827,
    roughness: 0.18,
    metalness: 0.55,
  });
  const paperMat = new THREE.MeshStandardMaterial({
    color: 0xf8fafc,
    roughness: 0.85,
  });
  const rugMat = new THREE.MeshStandardMaterial({
    color: 0x1e293b,
    roughness: 0.92,
  });

  const rug = new THREE.Mesh(new THREE.PlaneGeometry(4.6, 3.4), rugMat);
  rug.rotation.x = -Math.PI / 2;
  rug.position.y = 0.03;
  const rugEdge = new THREE.Mesh(
    new THREE.BoxGeometry(4.7, 0.015, 3.5),
    goldMat,
  );
  rugEdge.position.y = 0.025;
  lounge.add(rug, rugEdge);

  const sofa = new THREE.Group();
  const seat = new THREE.Mesh(
    new THREE.BoxGeometry(2.7, 0.32, 0.95),
    leatherMat,
  );
  seat.position.y = 0.38;
  const back = new THREE.Mesh(
    new THREE.BoxGeometry(2.7, 0.72, 0.22),
    leatherMat,
  );
  back.position.set(0, 0.78, -0.38);
  const armL = new THREE.Mesh(
    new THREE.BoxGeometry(0.22, 0.48, 0.95),
    leatherMat,
  );
  armL.position.set(-1.46, 0.5, 0);
  const armR = armL.clone();
  armR.position.x = 1.46;
  const sofaBase = new THREE.Mesh(
    new THREE.BoxGeometry(3.05, 0.06, 1.0),
    goldMat,
  );
  sofaBase.position.y = 0.08;
  for (const x of [-0.8, 0, 0.8]) {
    const cushion = new THREE.Mesh(
      new THREE.BoxGeometry(0.78, 0.12, 0.72),
      cushionMat,
    );
    cushion.position.set(x, 0.56, 0.04);
    sofa.add(cushion);
  }
  sofa.add(seat, back, armL, armR, sofaBase);
  sofa.position.set(0, 0, -0.85);
  lounge.add(sofa);

  const chair = new THREE.Group();
  const cSeat = new THREE.Mesh(
    new THREE.BoxGeometry(0.95, 0.28, 0.85),
    leatherMat,
  );
  cSeat.position.y = 0.36;
  const cBack = new THREE.Mesh(
    new THREE.BoxGeometry(0.95, 0.62, 0.16),
    leatherMat,
  );
  cBack.position.set(0, 0.7, -0.36);
  const cArmL = new THREE.Mesh(
    new THREE.BoxGeometry(0.14, 0.4, 0.85),
    leatherMat,
  );
  cArmL.position.set(-0.54, 0.46, 0);
  const cArmR = cArmL.clone();
  cArmR.position.x = 0.54;
  chair.add(cSeat, cBack, cArmL, cArmR);
  chair.position.set(1.55, 0, 0.55);
  chair.rotation.y = -0.85;
  lounge.add(chair);

  const table = new THREE.Group();
  const tableBase = new THREE.Mesh(
    new THREE.BoxGeometry(1.35, 0.22, 0.75),
    marbleTableMat,
  );
  tableBase.position.y = 0.2;
  const tableTop = new THREE.Mesh(
    new THREE.BoxGeometry(1.5, 0.04, 0.88),
    goldMat,
  );
  tableTop.position.y = 0.33;
  const openBook = new THREE.Mesh(
    new THREE.BoxGeometry(0.42, 0.02, 0.28),
    paperMat,
  );
  openBook.position.set(-0.18, 0.36, 0.05);
  const bookSpine = new THREE.Mesh(
    new THREE.BoxGeometry(0.02, 0.03, 0.28),
    goldMat,
  );
  bookSpine.position.set(-0.18, 0.365, 0.05);
  const closedBook = new THREE.Mesh(
    new THREE.BoxGeometry(0.22, 0.04, 0.3),
    new THREE.MeshStandardMaterial({ color: 0x991b1b, roughness: 0.55 }),
  );
  closedBook.position.set(0.32, 0.37, -0.08);
  closedBook.rotation.y = 0.35;
  table.add(tableBase, tableTop, openBook, bookSpine, closedBook);
  table.position.set(-0.15, 0, 0.35);
  lounge.add(table);

  const lampPole = new THREE.Mesh(
    new THREE.CylinderGeometry(0.03, 0.04, 1.55, 8),
    goldMat,
  );
  lampPole.position.set(-1.7, 0.78, 0.15);
  const lampShade = new THREE.Mesh(
    new THREE.ConeGeometry(0.22, 0.18, 12),
    new THREE.MeshStandardMaterial({
      color: 0xfde68a,
      emissive: 0xf59e0b,
      emissiveIntensity: 0.35,
    }),
  );
  lampShade.position.set(-1.7, 1.62, 0.15);
  const lampLight = new THREE.PointLight(0xfde68a, 0.9, 4.5, 2);
  lampLight.position.set(-1.7, 1.45, 0.15);
  lounge.add(lampPole, lampShade, lampLight);

  return lounge;
}

/** Small reading library: visible books, warm shelf light, no giant gold box */
function makeLibraryWall(accentCol) {
  const library = new THREE.Group();

  const woodMat = new THREE.MeshStandardMaterial({
    color: 0x3b2416,
    roughness: 0.55,
    metalness: 0.12,
  });
  const goldMat = new THREE.MeshStandardMaterial({
    color: 0xf59e0b,
    metalness: 0.85,
    roughness: 0.25,
  });
  const warmLed = new THREE.MeshBasicMaterial({
    color: 0xfde68a,
    toneMapped: false,
  });
  const bookColors = [
    0x991b1b, 0x1e3a8a, 0x065f46, 0x92400e, 0x4c1d95, 0xcbd5e1, 0x0f766e,
  ];
  const bookMats = bookColors.map(
    (c) => new THREE.MeshStandardMaterial({ color: c, roughness: 0.58 }),
  );

  const cabinet = new THREE.Mesh(
    new THREE.BoxGeometry(3.4, 3.15, 0.12),
    woodMat,
  );
  cabinet.position.set(0, 1.7, -0.28);
  const leftPost = new THREE.Mesh(
    new THREE.BoxGeometry(0.1, 3.2, 0.42),
    goldMat,
  );
  leftPost.position.set(-1.72, 1.7, -0.05);
  const rightPost = leftPost.clone();
  rightPost.position.x = 1.72;
  const topCap = new THREE.Mesh(
    new THREE.BoxGeometry(3.55, 0.1, 0.46),
    goldMat,
  );
  topCap.position.set(0, 3.28, -0.05);
  library.add(cabinet, leftPost, rightPost, topCap);

  const shelfYs = [0.28, 1.05, 1.82, 2.58];
  shelfYs.forEach((sy, i) => {
    const plank = new THREE.Mesh(
      new THREE.BoxGeometry(3.3, 0.07, 0.4),
      woodMat,
    );
    plank.position.set(0, sy, -0.04);
    library.add(plank);
    if (i < shelfYs.length - 1) {
      const led = new THREE.Mesh(
        new THREE.BoxGeometry(3.15, 0.015, 0.03),
        warmLed,
      );
      led.position.set(0, sy + 0.68, 0.14);
      library.add(led);
    }
  });

  const seed = [0.11, 0.19, 0.07, 0.23, 0.15, 0.09, 0.21, 0.13, 0.17];
  for (let s = 0; s < 3; s++) {
    let x = -1.5;
    let n = 0;
    while (x < 1.5) {
      const bw = 0.07 + seed[n % seed.length] * 0.18;
      const bh = 0.42 + seed[(n + 2) % seed.length] * 0.28;
      const book = new THREE.Mesh(
        new THREE.BoxGeometry(bw, bh, 0.22),
        bookMats[n % bookMats.length],
      );
      book.position.set(x + bw / 2, shelfYs[s] + 0.04 + bh / 2, 0.02);
      if (n % 7 === 6) book.rotation.z = -0.18;
      library.add(book);
      x += bw + 0.018;
      n += 1;
    }
  }

  const vase = new THREE.Mesh(
    new THREE.CylinderGeometry(0.08, 0.1, 0.22, 10),
    goldMat,
  );
  vase.position.set(0, 2.75, 0.02);
  library.add(vase);
  return library;
}

/** Animated indoor grand water wall: spanning the entire back wall behind reception */
function makeIndoorWaterfall(accentCol, wallWidth = 24.0, wallHeight = 8.8) {
  const fountain = new THREE.Group();

  const stoneMat = new THREE.MeshStandardMaterial({
    color: 0x1e2638,
    roughness: 0.85,
    metalness: 0.18,
  });
  const slateMat = new THREE.MeshStandardMaterial({
    color: 0x0f172a,
    roughness: 0.92,
    metalness: 0.1,
  });
  const brassMat = new THREE.MeshStandardMaterial({
    color: 0xd4af37,
    metalness: 0.82,
    roughness: 0.28,
  });
  const cyanGlowMat = new THREE.MeshBasicMaterial({
    color: 0x38bdf8,
    transparent: true,
    opacity: 0.85,
  });

  // Long rectangular stone basin across the entire wall width
  const basinWidth = wallWidth + 0.8;
  const basinDepth = 1.1;
  const basinHeight = 0.55;
  const basin = new THREE.Mesh(
    new THREE.BoxGeometry(basinWidth, basinHeight, basinDepth),
    stoneMat,
  );
  basin.position.set(0, basinHeight / 2, 0);

  // Basin brass top rim
  const basinRim = new THREE.Mesh(
    new THREE.BoxGeometry(basinWidth + 0.12, 0.06, basinDepth + 0.12),
    brassMat,
  );
  basinRim.position.set(0, basinHeight, 0);

  // Basin LED accent line along the front
  const basinLed = new THREE.Mesh(
    new THREE.BoxGeometry(basinWidth, 0.04, 0.04),
    cyanGlowMat,
  );
  basinLed.position.set(0, basinHeight - 0.06, basinDepth / 2 + 0.02);
  fountain.add(basin, basinRim, basinLed);

  // Textured Dark Slate Backdrop Wall
  const backDepth = 0.18;
  const back = new THREE.Mesh(
    new THREE.BoxGeometry(wallWidth, wallHeight, backDepth),
    slateMat,
  );
  back.position.set(
    0,
    wallHeight / 2 + basinHeight * 0.4,
    -basinDepth / 2 + backDepth / 2,
  );

  // Architectural Slate Fluted Vertical Panels along the backdrop
  const panelCount = Math.floor(wallWidth / 2.0);
  const dividerGeo = new THREE.BoxGeometry(0.06, wallHeight, 0.08);
  for (let i = -panelCount / 2; i <= panelCount / 2; i++) {
    const divider = new THREE.Mesh(dividerGeo, brassMat);
    divider.position.set(
      i * 2.0,
      wallHeight / 2 + basinHeight * 0.4,
      -basinDepth / 2 + backDepth + 0.02,
    );
    fountain.add(divider);
  }

  // Top Spout Ledge & Header across the full width
  const topLedge = new THREE.Mesh(
    new THREE.BoxGeometry(wallWidth + 0.4, 0.16, 0.45),
    brassMat,
  );
  topLedge.position.set(0, wallHeight + 0.1, -basinDepth / 2 + 0.18);

  const topGlow = new THREE.Mesh(
    new THREE.BoxGeometry(wallWidth, 0.04, 0.04),
    cyanGlowMat,
  );
  topGlow.position.set(0, wallHeight + 0.04, -basinDepth / 2 + 0.38);

  const topSpoutLip = new THREE.Mesh(
    new THREE.BoxGeometry(wallWidth, 0.08, 0.22),
    brassMat,
  );
  topSpoutLip.position.set(0, wallHeight - 0.02, -basinDepth / 2 + 0.26);
  fountain.add(back, topLedge, topGlow, topSpoutLip);

  // Flowing water sheet - Shader spanning the full wall
  const sheetUniforms = {
    uTime: { value: 0 },
    uColorA: { value: new THREE.Color(0x38bdf8) },
    uColorB: { value: new THREE.Color(0x0369a1) },
  };
  const sheetMat = new THREE.ShaderMaterial({
    uniforms: sheetUniforms,
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
    vertexShader: `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform float uTime;
      uniform vec3 uColorA;
      uniform vec3 uColorB;
      varying vec2 vUv;

      float hash(vec2 p) {
        return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
      }
      float noise(vec2 p) {
        vec2 i = floor(p);
        vec2 f = fract(p);
        float a = hash(i);
        float b = hash(i + vec2(1.0, 0.0));
        float c = hash(i + vec2(0.0, 1.0));
        float d = hash(i + vec2(1.0, 1.0));
        vec2 u = f * f * (3.0 - 2.0 * f);
        return mix(a, b, u.x) + (c - a) * u.y * (1.0 - u.x) + (d - b) * u.x * u.y;
      }

      void main() {
        float t = uTime;
        vec2 uv = vUv;
        float flow = uv.y + t * 1.45;
        // Horizontal repeat scaled by full wall aspect ratio
        float streaks = noise(vec2(uv.x * 64.0, flow * 9.0));
        float veins = noise(vec2(uv.x * 128.0 + sin(t * 1.8 + uv.x * 10.0) * 0.5, flow * 20.0));
        float foamTop = smoothstep(0.94, 1.0, uv.y);
        float foamBot = smoothstep(0.1, 0.0, uv.y);
        float edge = smoothstep(0.0, 0.02, uv.x) * smoothstep(1.0, 0.98, uv.x);
        float body = mix(0.4, 1.0, streaks);
        body *= mix(0.75, 1.2, veins);
        vec3 col = mix(uColorB, uColorA, body);
        col = mix(col, vec3(0.92, 0.98, 1.0), foamTop * 0.9 + foamBot * 0.75);
        float spark = step(0.92, noise(vec2(uv.x * 80.0, flow * 28.0)));
        col += spark * 0.45;
        float alpha = (0.45 + body * 0.38 + foamTop * 0.28 + foamBot * 0.25) * edge;
        gl_FragColor = vec4(col, alpha);
      }
    `,
  });

  const sheetGeometry = new THREE.PlaneGeometry(
    wallWidth * 0.98,
    wallHeight - 0.2,
    1,
    1,
  );
  const sheet = new THREE.Mesh(sheetGeometry, sheetMat);
  sheet.position.set(
    0,
    wallHeight / 2 + basinHeight * 0.35,
    -basinDepth / 2 + 0.24,
  );
  fountain.add(sheet);

  // Second slightly offset sheet for volumetric visual thickness
  const sheet2Mat = sheetMat.clone();
  sheet2Mat.uniforms = {
    uTime: sheetUniforms.uTime,
    uColorA: { value: new THREE.Color(0xa5f3fc) },
    uColorB: { value: new THREE.Color(0x0284c7) },
  };
  const sheet2 = new THREE.Mesh(sheetGeometry, sheet2Mat);
  sheet2.position.set(
    0,
    wallHeight / 2 + basinHeight * 0.35,
    -basinDepth / 2 + 0.28,
  );
  sheet2.scale.set(0.98, 1, 1);
  fountain.add(sheet2);

  // Rippling water surface inside the long basin
  const poolUniforms = { uTime: { value: 0 } };
  const poolMat = new THREE.ShaderMaterial({
    uniforms: poolUniforms,
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
    vertexShader: `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform float uTime;
      varying vec2 vUv;
      void main() {
        vec2 p = vUv * 2.0 - 1.0;
        // Multi-point ripple wave along the long basin width
        float rip = sin(p.x * 32.0 - uTime * 6.0) * sin(p.y * 14.0 + uTime * 4.0) * 0.5 + 0.5;
        float rip2 = sin(length(vec2(p.x * 4.0, p.y)) * 18.0 - uTime * 5.5) * 0.5 + 0.5;
        vec3 deep = vec3(0.02, 0.22, 0.38);
        vec3 lite = vec3(0.45, 0.88, 0.98);
        vec3 col = mix(deep, lite, rip * 0.5 + rip2 * 0.3);
        float foam = smoothstep(0.7, 0.98, abs(p.y));
        col = mix(col, vec3(0.9, 0.97, 1.0), foam * 0.6);
        float alpha = 0.85;
        gl_FragColor = vec4(col, alpha);
      }
    `,
  });

  const pool = new THREE.Mesh(
    new THREE.PlaneGeometry(basinWidth - 0.1, basinDepth - 0.1),
    poolMat,
  );
  pool.rotation.x = -Math.PI / 2;
  pool.position.set(0, basinHeight + 0.01, 0);
  fountain.add(pool);

  // Falling droplets across the wide wall
  const dropCount = 140;
  const dropGeo = new THREE.BufferGeometry();
  const dropPos = new Float32Array(dropCount * 3);
  const dropSeed = new Float32Array(dropCount);
  for (let i = 0; i < dropCount; i++) {
    dropPos[i * 3] = (Math.random() - 0.5) * (wallWidth * 0.94);
    dropPos[i * 3 + 1] = basinHeight + Math.random() * (wallHeight - 0.4);
    dropPos[i * 3 + 2] = -basinDepth / 2 + 0.25 + (Math.random() - 0.5) * 0.15;
    dropSeed[i] = Math.random();
  }
  dropGeo.setAttribute("position", new THREE.BufferAttribute(dropPos, 3));
  const dropMat = new THREE.PointsMaterial({
    color: 0xe0f2fe,
    size: 0.05,
    transparent: true,
    opacity: 0.85,
    depthWrite: false,
  });
  const drops = new THREE.Points(dropGeo, dropMat);
  fountain.add(drops);

  // Multi-point mist and glow illumination along the wide back wall
  for (const lx of [-8.0, 0, 8.0]) {
    const mistLight = new THREE.PointLight(0x38bdf8, 2.2, 14, 1.4);
    mistLight.position.set(lx, wallHeight * 0.45, 0.6);
    fountain.add(mistLight);
  }

  function update(t) {
    sheetUniforms.uTime.value = t;
    poolUniforms.uTime.value = t;
    const arr = dropGeo.attributes.position.array;
    for (let i = 0; i < dropCount; i++) {
      const speed = 1.8 + dropSeed[i] * 1.6;
      arr[i * 3 + 1] -= speed * 0.016;
      if (arr[i * 3 + 1] < basinHeight + 0.05) {
        arr[i * 3] = (Math.random() - 0.5) * (wallWidth * 0.94);
        arr[i * 3 + 1] = wallHeight - 0.1;
        arr[i * 3 + 2] = -basinDepth / 2 + 0.25 + (Math.random() - 0.5) * 0.15;
      }
    }
    dropGeo.attributes.position.needsUpdate = true;
  }

  fountain.userData.update = update;
  return fountain;
}

/** Standing concierge robot avatar at the lobby desk (matching server-room bot aesthetics) */
function makeReceptionistAvatar() {
  const avatar = new THREE.Group();

  // Pure dark industrial gray robot materials matching the server room bots (bots.js)
  const robotDarkMat = new THREE.MeshStandardMaterial({
    color: 0x1a2230, // Server-room bot dark chassis
    roughness: 0.7,
    metalness: 0.3,
  });
  const robotGrayMat = new THREE.MeshStandardMaterial({
    color: 0x475569, // Neutral slate gray metallic body panels
    roughness: 0.55,
    metalness: 0.4,
  });
  const robotLightGrayMat = new THREE.MeshStandardMaterial({
    color: 0x8f9599, // Bot hat/accent light steel gray
    roughness: 0.5,
    metalness: 0.25,
  });
  const chromeMat = new THREE.MeshStandardMaterial({
    color: 0x64748b, // Gunmetal chrome mechanical joints
    roughness: 0.35,
    metalness: 0.65,
  });
  const goldMat = new THREE.MeshStandardMaterial({
    color: 0xf59e0b,
    metalness: 0.85,
    roughness: 0.25,
  });
  const cyanVisorMat = new THREE.MeshBasicMaterial({
    color: 0x38bdf8,
    toneMapped: false,
    fog: false,
  });

  // 1. Robot Torso & Chest Armor (Dark gray industrial chassis)
  const torso = new THREE.Mesh(
    new THREE.BoxGeometry(0.56, 0.74, 0.3),
    robotDarkMat,
  );
  torso.position.y = 1.3;

  // Slate gray chestplate insert
  const chestPlate = new THREE.Mesh(
    new THREE.BoxGeometry(0.38, 0.46, 0.04),
    robotGrayMat,
  );
  chestPlate.position.set(0, 1.36, 0.14);

  // Vertical cyber core stripe
  const coreStripe = new THREE.Mesh(
    new THREE.BoxGeometry(0.04, 0.38, 0.02),
    cyanVisorMat,
  );
  coreStripe.position.set(0, 1.36, 0.165);

  // Steel gray shoulder pads
  for (const sx of [-0.3, 0.3]) {
    const pad = new THREE.Mesh(
      new THREE.BoxGeometry(0.12, 0.14, 0.28),
      robotLightGrayMat,
    );
    pad.position.set(sx, 1.62, 0);
    avatar.add(pad);
  }

  // Gold concierge ID badge on the chest
  const badge = new THREE.Mesh(
    new THREE.BoxGeometry(0.08, 0.04, 0.02),
    goldMat,
  );
  badge.position.set(0.14, 1.5, 0.17);

  // 2. Neck Joint & Sleek Robot Head with Glowing Cyan Visor Slit
  const neck = new THREE.Mesh(
    new THREE.CylinderGeometry(0.07, 0.08, 0.14, 12),
    chromeMat,
  );
  neck.position.y = 1.72;

  // Dark gray dome head
  const head = new THREE.Mesh(
    new THREE.SphereGeometry(0.18, 16, 14),
    robotDarkMat,
  );
  head.scale.set(0.92, 1.15, 0.96);
  head.position.y = 1.94;

  // Steel gray skull crown / helmet cap
  const skullCap = new THREE.Mesh(
    new THREE.SphereGeometry(0.185, 16, 12, 0, Math.PI * 2, 0, Math.PI / 2),
    robotLightGrayMat,
  );
  skullCap.position.y = 1.96;

  // Glowing Cyan Visor Slit (exact signature server-room bot visor)
  const visor = new THREE.Mesh(
    new THREE.PlaneGeometry(0.24, 0.05),
    cyanVisorMat,
  );
  visor.position.set(0, 1.94, 0.175);

  // Ear antenna sensor nodes
  for (const ex of [-0.17, 0.17]) {
    const ear = new THREE.Mesh(
      new THREE.CylinderGeometry(0.025, 0.025, 0.06, 8),
      chromeMat,
    );
    ear.rotation.z = Math.PI / 2;
    ear.position.set(ex, 1.94, 0.02);
    avatar.add(ear);
  }

  // 3. Legs and Lower Body in Slate Gray
  const hip = new THREE.Mesh(
    new THREE.BoxGeometry(0.48, 0.18, 0.26),
    robotDarkMat,
  );
  hip.position.y = 0.88;

  const legL = new THREE.Mesh(
    new THREE.BoxGeometry(0.18, 0.82, 0.2),
    robotGrayMat,
  );
  legL.position.set(-0.14, 0.42, 0);
  const legR = legL.clone();
  legR.position.x = 0.14;

  // Knee mechanical caps
  for (const kx of [-0.14, 0.14]) {
    const knee = new THREE.Mesh(
      new THREE.CylinderGeometry(0.045, 0.045, 0.08, 8),
      chromeMat,
    );
    knee.rotation.z = Math.PI / 2;
    knee.position.set(kx, 0.42, 0.1);
    avatar.add(knee);
  }

  // Metallic robot feet
  const footMat = new THREE.MeshStandardMaterial({
    color: 0x1e293b,
    roughness: 0.4,
    metalness: 0.6,
  });
  const footL = new THREE.Mesh(new THREE.BoxGeometry(0.19, 0.1, 0.28), footMat);
  footL.position.set(-0.14, 0.05, 0.04);
  const footR = footL.clone();
  footR.position.x = 0.14;

  // 4. Arms & Posed Mechanical Hands
  const armL = new THREE.Mesh(
    new THREE.CylinderGeometry(0.065, 0.055, 0.58, 10),
    robotGrayMat,
  );
  armL.position.set(-0.35, 1.25, 0.02);
  armL.rotation.z = 0.12;
  const handL = new THREE.Mesh(
    new THREE.SphereGeometry(0.055, 10, 10),
    chromeMat,
  );
  handL.position.set(0, -0.32, 0);
  armL.add(handL);

  const armR = new THREE.Mesh(
    new THREE.CylinderGeometry(0.065, 0.055, 0.58, 10),
    robotGrayMat,
  );
  armR.position.set(0.36, 1.28, 0.08);
  armR.rotation.set(0.48, 0.1, -0.38);
  const handR = new THREE.Mesh(
    new THREE.SphereGeometry(0.055, 10, 10),
    chromeMat,
  );
  handR.position.set(0, -0.32, 0);
  armR.add(handR);

  // Dedicated avatar spotlight for crisp clarity against the back waterfall
  const avatarKeyLight = new THREE.SpotLight(
    0xf1f5f9,
    3.8,
    8.0,
    Math.PI / 4,
    0.35,
  );
  avatarKeyLight.position.set(0.4, 2.8, 1.8);
  avatarKeyLight.target = torso;

  avatar.add(
    torso,
    chestPlate,
    coreStripe,
    badge,
    neck,
    head,
    skullCap,
    visor,
    hip,
    legL,
    legR,
    footL,
    footR,
    armL,
    armR,
    avatarKeyLight,
    avatarKeyLight.target,
  );
  return avatar;
}

/** Grand Luxury Reception / Concierge Desk (interactive floor selector) */
function makeReceptionDesk(accentCol) {
  const desk = new THREE.Group();
  desk.name = "reception_desk";

  const marbleMat = new THREE.MeshStandardMaterial({
    color: 0x0d141f,
    roughness: 0.15,
    metalness: 0.6,
  });
  const goldMat = new THREE.MeshStandardMaterial({
    color: 0xf59e0b,
    metalness: 0.85,
    roughness: 0.25,
  });
  const cyanGlow = new THREE.MeshBasicMaterial({
    color: accentCol,
    toneMapped: false,
  });
  const screenMat = new THREE.MeshBasicMaterial({
    color: 0xe0f2fe,
    toneMapped: false,
  });

  const counterBody = new THREE.Mesh(
    new THREE.BoxGeometry(3.6, 1.05, 1.15),
    marbleMat,
  );
  counterBody.position.y = 0.53;
  const frontRibbon = new THREE.Mesh(
    new THREE.BoxGeometry(3.4, 0.04, 0.04),
    cyanGlow,
  );
  frontRibbon.position.set(0, 0.16, 0.59);
  const topRim = new THREE.Mesh(
    new THREE.BoxGeometry(3.8, 0.07, 1.25),
    marbleMat,
  );
  topRim.position.y = 1.08;
  const goldTrim = new THREE.Mesh(
    new THREE.BoxGeometry(3.7, 0.03, 1.2),
    goldMat,
  );
  goldTrim.position.y = 1.05;

  const consoleBase = new THREE.Mesh(
    new THREE.BoxGeometry(0.7, 0.04, 0.42),
    goldMat,
  );
  consoleBase.position.set(0.55, 1.13, 0.08);
  const screen = new THREE.Mesh(new THREE.PlaneGeometry(0.58, 0.36), screenMat);
  screen.rotation.x = -0.4;
  screen.position.set(0.55, 1.34, 0.02);
  const holoRing = new THREE.Mesh(
    new THREE.TorusGeometry(0.18, 0.012, 8, 20),
    cyanGlow,
  );
  holoRing.rotation.x = Math.PI / 2;
  holoRing.position.set(0.55, 1.55, 0.02);

  const avatar = makeReceptionistAvatar();
  avatar.position.set(-0.15, 0, -0.22);
  avatar.rotation.y = 0.08;

  desk.add(
    counterBody,
    frontRibbon,
    topRim,
    goldTrim,
    consoleBase,
    screen,
    holoRing,
    avatar,
  );
  desk.userData = { isReception: true, interactive: true };
  return desk;
}

/** Grand Lobby Interior Environment with Cathedral Height Ceiling, Marble Floor, Pillars & Lounge */
/** Luxury architectural indoor palm: high-end faceted ceramic/gold planter, textured segmented trunk, lush arched 3D fronds */
function makeLobbyPalm(accentCol) {
  const palm = new THREE.Group();

  // Materials
  const potOuterMat = new THREE.MeshStandardMaterial({
    color: 0x0f172a, // Deep slate luxury gloss pot
    roughness: 0.15,
    metalness: 0.65,
  });
  const goldAccentMat = new THREE.MeshStandardMaterial({
    color: 0xf59e0b,
    metalness: 0.9,
    roughness: 0.2,
  });
  const soilMat = new THREE.MeshStandardMaterial({
    color: 0x1c1917,
    roughness: 0.95,
  });
  const trunkMat = new THREE.MeshStandardMaterial({
    color: 0x451a03, // Rich palm bark tone
    roughness: 0.85,
    metalness: 0.1,
  });
  const frondBrightMat = new THREE.MeshStandardMaterial({
    color: 0x22c55e, // Fresh tropical emerald
    roughness: 0.45,
    metalness: 0.1,
    side: THREE.DoubleSide,
  });
  const frondDeepMat = new THREE.MeshStandardMaterial({
    color: 0x15803d, // Deep underside/core green
    roughness: 0.5,
    metalness: 0.1,
    side: THREE.DoubleSide,
  });
  const stemMat = new THREE.MeshStandardMaterial({
    color: 0x84cc16, // Lime green arching stem
    roughness: 0.6,
  });

  // 1. Luxury Pot / Planter: Octagonal architectural vase with dual gold bands
  const potGeo = new THREE.CylinderGeometry(0.52, 0.36, 0.9, 12);
  const pot = new THREE.Mesh(potGeo, potOuterMat);
  pot.position.y = 0.45;

  const potBase = new THREE.Mesh(
    new THREE.CylinderGeometry(0.38, 0.42, 0.08, 12),
    goldAccentMat,
  );
  potBase.position.y = 0.04;

  const goldRimTop = new THREE.Mesh(
    new THREE.TorusGeometry(0.53, 0.035, 10, 24),
    goldAccentMat,
  );
  goldRimTop.rotation.x = Math.PI / 2;
  goldRimTop.position.y = 0.9;

  const goldRibbonMid = new THREE.Mesh(
    new THREE.TorusGeometry(0.46, 0.02, 8, 24),
    goldAccentMat,
  );
  goldRibbonMid.rotation.x = Math.PI / 2;
  goldRibbonMid.position.y = 0.55;

  const soil = new THREE.Mesh(
    new THREE.CylinderGeometry(0.5, 0.48, 0.05, 12),
    soilMat,
  );
  soil.position.y = 0.86;

  palm.add(potBase, pot, goldRimTop, goldRibbonMid, soil);

  // 2. Trunk: Stacked natural diamond palm rings
  const trunk = new THREE.Group();
  const ringCount = 14;
  for (let r = 0; r < ringCount; r++) {
    const progress = r / ringCount;
    const rY = 0.88 + r * 0.14;
    const radius = 0.15 - progress * 0.05;
    const lean = Math.sin(progress * Math.PI * 0.8) * 0.08;
    const ring = new THREE.Mesh(
      new THREE.CylinderGeometry(radius * 0.92, radius * 1.05, 0.15, 8),
      trunkMat,
    );
    ring.position.set(lean, rY, lean * 0.5);
    ring.rotation.z = -lean * 0.6;
    ring.rotation.y = r * 0.7;
    trunk.add(ring);
  }
  palm.add(trunk);

  // 3. Lush Volumetric Palm Canopy
  const canopy = new THREE.Group();
  canopy.position.set(0.06, 2.75, 0.03);

  // Helper function to build a multi-ribbed arched 3D palm branch
  function buildArchedFrond(length, width, leaflets, archAngle, colorMode) {
    const branch = new THREE.Group();
    const subSegs = 6;
    const dL = length / subSegs;

    let prevPos = new THREE.Vector3(0, 0, 0);
    let curAngle = 0;

    for (let s = 0; s < subSegs; s++) {
      const segProgress = s / subSegs;
      curAngle += (archAngle / subSegs) * (1 + segProgress * 0.8);
      const dy = Math.cos(curAngle) * dL;
      const dz = Math.sin(curAngle) * dL;

      const newPos = new THREE.Vector3(
        prevPos.x,
        prevPos.y + dy,
        prevPos.z + dz,
      );

      // Central stem segment
      const stemThick = 0.035 * (1 - segProgress * 0.7);
      const stemGeo = new THREE.CylinderGeometry(
        stemThick * 0.8,
        stemThick,
        dL,
        5,
      );
      const stem = new THREE.Mesh(stemGeo, stemMat);
      stem.position.copy(prevPos).lerp(newPos, 0.5);
      stem.rotation.x = curAngle;
      branch.add(stem);

      // Leaflet pairs flanking this segment
      if (s >= 1) {
        const leafW = width * Math.sin(segProgress * Math.PI);
        const leafL = dL * 1.6;
        const mat = (s + colorMode) % 2 === 0 ? frondBrightMat : frondDeepMat;

        // Left leaflet
        const leafLMesh = new THREE.Mesh(
          new THREE.ConeGeometry(leafW * 0.45, leafL, 4),
          mat,
        );
        leafLMesh.position.copy(stem.position);
        leafLMesh.position.x -= leafW * 0.5;
        leafLMesh.rotation.set(
          curAngle + 0.15,
          0.2,
          -0.65 - segProgress * 0.35,
        );
        branch.add(leafLMesh);

        // Right leaflet
        const leafRMesh = new THREE.Mesh(
          new THREE.ConeGeometry(leafW * 0.45, leafL, 4),
          mat,
        );
        leafRMesh.position.copy(stem.position);
        leafRMesh.position.x += leafW * 0.5;
        leafRMesh.rotation.set(
          curAngle + 0.15,
          -0.2,
          0.65 + segProgress * 0.35,
        );
        branch.add(leafRMesh);
      }

      prevPos.copy(newPos);
    }

    return branch;
  }

  // Tier 1: Outer weeping layer (8 wide drooping fronds)
  const outerCount = 8;
  for (let i = 0; i < outerCount; i++) {
    const angle = (i / outerCount) * Math.PI * 2 + 0.15;
    const frond = buildArchedFrond(1.85, 0.48, 8, 1.45, i);
    frond.rotation.y = angle;
    canopy.add(frond);
  }

  // Tier 2: Mid spreading layer (7 lush elevated fronds)
  const midCount = 7;
  for (let i = 0; i < midCount; i++) {
    const angle = (i / midCount) * Math.PI * 2 + 0.45;
    const frond = buildArchedFrond(1.6, 0.42, 6, 0.95, i + 1);
    frond.position.y = 0.12;
    frond.rotation.y = angle;
    canopy.add(frond);
  }

  // Tier 3: Inner crown shoots (5 fresh upright fronds)
  const innerCount = 5;
  for (let i = 0; i < innerCount; i++) {
    const angle = (i / innerCount) * Math.PI * 2 + 0.3;
    const frond = buildArchedFrond(1.25, 0.32, 5, 0.45, i + 2);
    frond.position.y = 0.22;
    frond.rotation.y = angle;
    canopy.add(frond);
  }

  palm.add(canopy);
  return palm;
}

function makeLobbyInterior(accentCol) {
  const lobby = new THREE.Group();

  const floorMat = new THREE.MeshStandardMaterial({
    color: 0x080c14, // Reflective dark obsidian granite floor
    roughness: 0.2,
    metalness: 0.5,
  });
  const wallMat = new THREE.MeshStandardMaterial({
    color: 0x0a101d, // Luxury dark architectural lobby wall
    roughness: 0.85,
    metalness: 0.2,
  });
  const goldPillarMat = new THREE.MeshStandardMaterial({
    color: 0x1e293b,
    metalness: 0.8,
    roughness: 0.3,
  });
  const goldMat = new THREE.MeshStandardMaterial({
    color: 0xf59e0b,
    metalness: 0.85,
    roughness: 0.25,
  });
  const cyanGlow = new THREE.MeshBasicMaterial({
    color: accentCol,
    toneMapped: false,
  });

  // 1. Polished Lobby Granite Floor (W: 28, D: 22) at Y: 0.18
  const floor = new THREE.Mesh(new THREE.PlaneGeometry(28, 22), floorMat);
  floor.rotation.x = -Math.PI / 2;
  floor.position.set(0, 0.18, 5);
  lobby.add(floor);

  // 2. Full Enclosed Solid Architectural Perimeter Walls & Ceiling for Ground Lobby (Y: 0 to 9.8)
  const W = 27.6,
    H = 9.8,
    D = 21.6;

  // Back Wall (North, behind reception at Z = -5.8)
  const backWall = new THREE.Mesh(new THREE.BoxGeometry(W, H, 0.3), wallMat);
  backWall.position.set(0, H / 2, -5.8);
  lobby.add(backWall);

  // West Wall (Left side at X = -13.8)
  const westWall = new THREE.Mesh(new THREE.BoxGeometry(0.3, H, D), wallMat);
  westWall.position.set(-W / 2, H / 2, 5);
  lobby.add(westWall);

  // East Wall (Right side at X = 13.8)
  const eastWall = new THREE.Mesh(new THREE.BoxGeometry(0.3, H, D), wallMat);
  eastWall.position.set(W / 2, H / 2, 5);
  lobby.add(eastWall);

  // Front wall split around the grand door opening
  const frontLeft = new THREE.Mesh(new THREE.BoxGeometry(8.2, H, 0.3), wallMat);
  frontLeft.position.set(-9.6, H / 2, 15.7);
  const frontRight = new THREE.Mesh(
    new THREE.BoxGeometry(8.2, H, 0.3),
    wallMat,
  );
  frontRight.position.set(9.6, H / 2, 15.7);
  const frontHeader = new THREE.Mesh(
    new THREE.BoxGeometry(11.4, 1.4, 0.3),
    wallMat,
  );
  frontHeader.position.set(0, 9.0, 15.7);
  lobby.add(frontLeft, frontRight, frontHeader);

  // Ceiling Slab (underneath Floor 1 at Y = 9.7)
  const ceiling = new THREE.Mesh(new THREE.BoxGeometry(W, 0.3, D), wallMat);
  ceiling.position.set(0, 9.7, 5);
  lobby.add(ceiling);

  // Decorative Wall Baseboards & Crown Molding in Gold
  const baseTrimBack = new THREE.Mesh(
    new THREE.BoxGeometry(W, 0.15, 0.35),
    goldMat,
  );
  baseTrimBack.position.set(0, 0.08, -5.75);
  const crownTrimBack = new THREE.Mesh(
    new THREE.BoxGeometry(W, 0.15, 0.35),
    goldMat,
  );
  crownTrimBack.position.set(0, 9.55, -5.75);
  lobby.add(baseTrimBack, crownTrimBack);

  // Modern High-Tech / Architectural Corporate Carpet Runner (Entrance Z:15.5 to Reception Desk Z:2.4)
  const carpetGroup = new THREE.Group();
  const carpetMat = new THREE.MeshStandardMaterial({
    color: 0x1d4ed8, // Classic rich corporate blue (non-glowing)
    roughness: 0.8,
    metalness: 0.1,
  });
  const carpetDarkNavyMat = new THREE.MeshStandardMaterial({
    color: 0x0f172a, // Deep dark navy inner runner
    roughness: 0.85,
    metalness: 0.05,
  });
  const carpetGoldMat = new THREE.MeshStandardMaterial({
    color: 0xf59e0b, // Polished luxury gold border
    metalness: 0.85,
    roughness: 0.25,
  });

  const carpetW = 3.8;
  const carpetL = 13.6;
  const carpetCenterZ = 9.0; // spans Z: 2.2 to 15.8

  // 1. Base corporate blue runner slab (Y: 0.20 to sit clearly above the floor at Y: 0.18)
  const carpetMain = new THREE.Mesh(
    new THREE.BoxGeometry(carpetW, 0.04, carpetL),
    carpetMat,
  );
  carpetMain.position.set(0, 0.2, carpetCenterZ);
  carpetGroup.add(carpetMain);

  // 2. Deep dark navy center field
  const carpetInner = new THREE.Mesh(
    new THREE.BoxGeometry(carpetW - 0.6, 0.042, carpetL - 0.6),
    carpetDarkNavyMat,
  );
  carpetInner.position.set(0, 0.204, carpetCenterZ);
  carpetGroup.add(carpetInner);

  // 3. Polished gold perimeter borders
  const borderGeo = new THREE.BoxGeometry(0.12, 0.046, carpetL);
  const borderL = new THREE.Mesh(borderGeo, carpetGoldMat);
  borderL.position.set(-carpetW / 2 + 0.06, 0.208, carpetCenterZ);
  const borderR = borderL.clone();
  borderR.position.x = carpetW / 2 - 0.06;
  carpetGroup.add(borderL, borderR);

  // 4. Subtle dark navy/blue geometric pattern stripes (pure material, no glowing neon)
  const stripeGeo = new THREE.BoxGeometry(carpetW - 1.0, 0.044, 0.18);
  for (let z = 3.2; z <= 15.0; z += 1.6) {
    const stripe = new THREE.Mesh(stripeGeo, carpetMat);
    stripe.position.set(0, 0.206, z);
    carpetGroup.add(stripe);
  }

  // 5. Welcome threshold plate in gold at entrance
  const welcomeBand = new THREE.Mesh(
    new THREE.BoxGeometry(carpetW + 0.2, 0.05, 0.4),
    carpetGoldMat,
  );
  welcomeBand.position.set(0, 0.21, 15.6);
  carpetGroup.add(welcomeBand);

  // 6. Front desk terminus plate in gold
  const deskBand = new THREE.Mesh(
    new THREE.BoxGeometry(carpetW + 0.2, 0.05, 0.3),
    carpetGoldMat,
  );
  deskBand.position.set(0, 0.21, 2.3);
  carpetGroup.add(deskBand);

  lobby.add(carpetGroup);

  // 3. Slim architectural columns - keep the aisle open
  const pillarGeo = new THREE.BoxGeometry(0.45, 9.4, 0.45);
  for (const px of [-9.2, 9.2]) {
    for (const pz of [-1.5, 8.5]) {
      const p = new THREE.Mesh(pillarGeo, goldPillarMat);
      p.position.set(px, 4.7, pz);
      lobby.add(p);
    }
  }

  // 4. Reception desk + standing avatar, center, facing the door
  const reception = makeReceptionDesk(accentCol);
  reception.position.set(0, 0, 1.2);
  lobby.add(reception);

  // 5. Sofa / reading lounge on the left, facing the desk
  const lounge = makeLoungeArea(accentCol);
  lounge.position.set(-5.6, 0, 4.8);
  lounge.rotation.y = 0.28;
  lobby.add(lounge);

  // 6. Small library bookcase just behind the lounge
  const library = makeLibraryWall(accentCol);
  library.position.set(-7.4, 0, 2.2);
  library.rotation.y = 0.4;
  lobby.add(library);

  // 6b. Mirrored copies of the lounge & library on the right side (exact match)
  const loungeR = lounge.clone();
  loungeR.position.set(5.6, 0, 4.8);
  loungeR.rotation.y = -0.28;
  lobby.add(loungeR);

  const libraryR = library.clone();
  libraryR.position.set(7.4, 0, 2.2);
  libraryR.rotation.y = -0.4;
  lobby.add(libraryR);

  // 6c. Two grand palm trees flanking the reception desk
  const palmL = makeLobbyPalm(accentCol);
  palmL.position.set(-2.9, 0, 1.3);
  const palmR = makeLobbyPalm(accentCol);
  palmR.position.set(2.9, 0, 1.3);
  lobby.add(palmL, palmR);

  // 7. Grand animated water wall spanning the entire back wall behind reception (Z: -5.2)
  const waterfall = makeIndoorWaterfall(accentCol, 26.0, 9.2);
  waterfall.position.set(0, 0, -5.1);
  lobby.add(waterfall);

  // 8. High Suspended Grand Ring Chandelier & Downlights (Y: 8.5)
  const chandelierGroup = new THREE.Group();
  const ringGeo = new THREE.TorusGeometry(3.2, 0.08, 12, 48);
  const ringMat = new THREE.MeshStandardMaterial({
    color: 0xf59e0b,
    metalness: 0.9,
    roughness: 0.2,
  });
  const ringMesh = new THREE.Mesh(ringGeo, ringMat);
  ringMesh.rotation.x = Math.PI / 2;
  const innerRing = new THREE.Mesh(
    new THREE.TorusGeometry(2.0, 0.05, 8, 36),
    cyanGlow,
  );
  innerRing.rotation.x = Math.PI / 2;
  chandelierGroup.add(ringMesh, innerRing);
  chandelierGroup.position.set(0, 7.8, 3.5);
  lobby.add(chandelierGroup);

  const lobbyLight = new THREE.PointLight(0xe0f2fe, 4.2, 26, 1.3);
  lobbyLight.position.set(0, 7.2, 3.5);
  lobby.add(lobbyLight);

  return { lobbyGroup: lobby, reception, waterfall };
}

/** Current local hour in Israel (DST handled by the Intl timezone). */
function israelHour() {
  try {
    return Number(
      new Intl.DateTimeFormat("en-GB", {
        timeZone: "Asia/Jerusalem",
        hour: "2-digit",
        hour12: false,
      }).format(new Date()),
    );
  } catch (_) {
    const d = new Date();
    return (d.getUTCHours() + 3) % 24;
  }
}

function isDayInIsrael() {
  if (typeof window !== "undefined") {
    const params = new URLSearchParams(window.location.search);
    if (params.get("night") === "1" || params.get("time") === "night")
      return false;
    if (params.get("day") === "1" || params.get("time") === "day") return true;
  }
  const h = israelHour();
  return h >= 6 && h < 19;
}

/** Cyber streetlight with glowing luminaire */
function makeStreetLight(warmGlowMat, isDay) {
  const lightGroup = new THREE.Group();
  const poleMat = new THREE.MeshStandardMaterial({
    color: 0x1e293b,
    metalness: 0.8,
    roughness: 0.3,
  });

  // Vertical pole
  const pole = new THREE.Mesh(
    new THREE.CylinderGeometry(0.08, 0.12, 5.5, 8),
    poleMat,
  );
  pole.position.y = 2.75;

  // Overhanging mast arm
  const arm = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.08, 1.4), poleMat);
  arm.position.set(0, 5.4, 0.6);

  // Glowing lamp head (warm golden-amber at night)
  const lamp = new THREE.Mesh(
    new THREE.BoxGeometry(0.3, 0.12, 0.6),
    warmGlowMat,
  );
  lamp.position.set(0, 5.3, 1.2);

  // Warm golden amber street light point source (#ffaa22 / #f59e0b)
  const spot = new THREE.PointLight(0xffaa22, isDay ? 0 : 3.8, 16, 1.4);
  spot.position.set(0, 5.0, 1.2);

  lightGroup.add(pole, arm, lamp, spot);
  return lightGroup;
}

export function buildStreet(scene) {
  const isDay = isDayInIsrael();
  const accentCol = isDay ? 0x0284c7 : 0x38bdf8;
  const streetGroup = new THREE.Group();
  streetGroup.position.set(0, 0, 0);

  // 1. Asphalt Roadway & Markings
  const roadGeo = new THREE.PlaneGeometry(160, 24);
  const roadMat = new THREE.MeshStandardMaterial({
    color: 0x090d14,
    roughness: 0.85,
    metalness: 0.15,
  });
  const road = new THREE.Mesh(roadGeo, roadMat);
  road.rotation.x = -Math.PI / 2;
  road.position.set(0, 0.01, 26);
  streetGroup.add(road);

  // Road Lane Dividers (Dashed glowing line)
  const lineMat = new THREE.MeshBasicMaterial({
    color: 0xf59e0b,
    toneMapped: false,
  });
  for (let x = -60; x <= 60; x += 10) {
    const dash = new THREE.Mesh(new THREE.PlaneGeometry(5, 0.3), lineMat);
    dash.rotation.x = -Math.PI / 2;
    dash.position.set(x, 0.02, 26);
    streetGroup.add(dash);
  }

  // 2. Long Concrete Sidewalk Plaza
  // Extended from the building base (Z: 12) well past the street camera
  // (Z: 34) all the way to Z: 48 - one continuous solid ground so the tower
  // never looks like it is floating on a short strip. The old asphalt road
  // (Z: 14-38) sits underneath the plaza and is no longer visible.
  const sidewalkGeo = new THREE.BoxGeometry(160, 0.35, 36);
  const sidewalkMat = new THREE.MeshStandardMaterial({
    color: 0x18202c,
    roughness: 0.6,
    metalness: 0.2,
  });
  const sidewalk = new THREE.Mesh(sidewalkGeo, sidewalkMat);
  sidewalk.position.set(0, 0.18, 30);
  streetGroup.add(sidewalk);

  // Curb curbstone rim at the far edge of the long plaza (Z: 48)
  const curbGeo = new THREE.BoxGeometry(160, 0.42, 0.4);
  const curbMat = new THREE.MeshStandardMaterial({
    color: 0x334155,
    roughness: 0.5,
  });
  const curb = new THREE.Mesh(curbGeo, curbMat);
  curb.position.set(0, 0.21, 47.8);
  streetGroup.add(curb);

  // 2b. Ground behind the building (Z: -100 -> 12)
  // The front plaza only covers Z: 12-48, so from behind/at the sides of the
  // tower there was no ground at all - just sky dome, which read as "air".
  // This plane extends the floor from the building's front edge (Z: 12) all
  // the way behind the tower, so the ground never disappears. It sits at
  // Y: 0.005 (just above the hidden road, below the plaza top 0.355) so the
  // lobby interior floor (Y: 0.18) is never covered.
  const backGroundGeo = new THREE.PlaneGeometry(200, 112);
  const backGroundMat = new THREE.MeshStandardMaterial({
    color: 0x18202c,
    roughness: 0.6,
    metalness: 0.2,
  });
  const backGround = new THREE.Mesh(backGroundGeo, backGroundMat);
  backGround.rotation.x = -Math.PI / 2;
  backGround.position.set(0, 0.005, -44);
  streetGroup.add(backGround);

  // 3. Cyber Streetlights along curb (warm golden amber glow at night)
  const warmGlowMat = new THREE.MeshBasicMaterial({
    color: isDay ? 0x94a3b8 : 0xffaa22,
    toneMapped: false,
  });
  const lightPositions = [-45, -25, -6, 6, 25, 45];
  lightPositions.forEach((lx) => {
    const sl = makeStreetLight(warmGlowMat, isDay);
    sl.position.set(lx, 0.35, 19.2);
    streetGroup.add(sl);
  });

  // 4. Hot Dog Stand & Cyber Cafe on the Plaza
  const hotDogStand = makeHotDogStand();
  hotDogStand.position.set(-9.5, 0.35, 13.5);
  hotDogStand.rotation.y = 0.4;
  streetGroup.add(hotDogStand);

  const cafe1 = makeCafeTerrace();
  cafe1.position.set(10.5, 0.35, 12.5);
  const cafe2 = makeCafeTerrace();
  cafe2.position.set(14.2, 0.35, 14.0);
  streetGroup.add(cafe1, cafe2);

  // 5. Low-Poly Pedestrians Walking Along the Open Sidewalk (Z: 17.2 - 17.8, clear of building walls & cafe)
  const peds = [
    {
      mesh: makePedestrian(0x38bdf8, 0xd4a373, 0x1e293b),
      x: -16,
      z: 17.5,
      speed: 1.6,
      dir: 1,
      minX: -42,
      maxX: 42,
      phase: 0,
    },
    {
      mesh: makePedestrian(0x3ddc84, 0xfbcfe8, 0x334155),
      x: 20,
      z: 17.2,
      speed: 1.8,
      dir: -1,
      minX: -42,
      maxX: 42,
      phase: 1.4,
    },
    // Customer waiting by the hot dog stand
    {
      mesh: makePedestrian(0xf59e0b, 0xd4a373, 0x0f172a),
      x: -8.2,
      z: 14.5,
      speed: 0,
      dir: 1,
      static: true,
      phase: 0.5,
    },
  ];

  peds.forEach((p) => {
    p.mesh.position.set(p.x, 0.35, p.z);
    if (p.dir === -1) p.mesh.rotation.y = -Math.PI / 2;
    else if (p.dir === 1 && !p.static) p.mesh.rotation.y = Math.PI / 2;
    else if (p.static) p.mesh.rotation.y = -0.9;
    streetGroup.add(p.mesh);
  });

  // 6. Cyber Planters & Trees
  const planterMat = new THREE.MeshStandardMaterial({
    color: 0x1e293b,
    roughness: 0.7,
  });
  const foliageMat = new THREE.MeshStandardMaterial({
    color: 0x064e3b,
    roughness: 0.9,
  });
  function makeCyberTree(tx, tz) {
    const treeGroup = new THREE.Group();
    const planter = new THREE.Mesh(
      new THREE.BoxGeometry(1.4, 0.6, 1.4),
      planterMat,
    );
    planter.position.y = 0.3;
    const trunk = new THREE.Mesh(
      new THREE.CylinderGeometry(0.1, 0.12, 1.8, 6),
      planterMat,
    );
    trunk.position.y = 1.2;
    const foliage = new THREE.Mesh(
      new THREE.DodecahedronGeometry(0.9, 1),
      foliageMat,
    );
    foliage.position.y = 2.4;
    treeGroup.add(planter, trunk, foliage);
    treeGroup.position.set(tx, 0.35, tz);
    return treeGroup;
  }
  streetGroup.add(makeCyberTree(-18, 12));
  streetGroup.add(makeCyberTree(-30, 12));
  streetGroup.add(makeCyberTree(24, 12));
  streetGroup.add(makeCyberTree(40, 12));

  // 7. Grand Lobby Interior & Grand Portal
  const { lobbyGroup, reception, waterfall } = makeLobbyInterior(accentCol);
  streetGroup.add(lobbyGroup);

  // Grand Entrance Portal at the lobby front (Z = 15.6)
  const grandEntrance = makeGrandEntrance(accentCol);
  grandEntrance.position.set(0, 0, 15.6);
  streetGroup.add(grandEntrance);

  // Security Guard stationed at lobby entrance (Z = 13.2, X = -2.8)
  const securityGuard = makeSecurityGuard(accentCol);
  securityGuard.position.set(-3.4, 0, 15.0);
  securityGuard.rotation.y = 0.25;
  streetGroup.add(securityGuard);

  scene.add(streetGroup);

  /** Frame update for walking pedestrians - only active when street level is visible / active */
  function update(t, delta, isStreetActive) {
    // Frame freezing optimization: When camera is on upper floors, freeze animated transforms to save GPU/CPU cycles
    if (!isStreetActive) return;

    if (waterfall && waterfall.userData && waterfall.userData.update) {
      waterfall.userData.update(t);
    }

    peds.forEach((p) => {
      if (p.static) return;

      // Advance position along sidewalk
      p.x += p.dir * p.speed * delta;
      if (p.x > p.maxX) {
        p.x = p.maxX;
        p.dir = -1;
        p.mesh.rotation.y = -Math.PI / 2;
      } else if (p.x < p.minX) {
        p.x = p.minX;
        p.dir = 1;
        p.mesh.rotation.y = Math.PI / 2;
      }
      p.mesh.position.x = p.x;

      // Leg & Arm stride animation
      const walkCycle = Math.sin((t + p.phase) * 6.5 * (p.speed / 2.0));
      if (p.mesh.userData.legL && p.mesh.userData.legR) {
        p.mesh.userData.legL.rotation.x = walkCycle * 0.55;
        p.mesh.userData.legR.rotation.x = -walkCycle * 0.55;
      }
      if (p.mesh.userData.armL && p.mesh.userData.armR) {
        p.mesh.userData.armL.rotation.x = -walkCycle * 0.45;
        p.mesh.userData.armR.rotation.x = walkCycle * 0.45;
      }
    });
  }

  return {
    streetGroup,
    reception,
    update,
  };
}
